import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import {
    Alert,
    Grid,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';

import {
    FlexiCloseButton,
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';

import {Profile} from '../components/Profile';
import {DetailPageNavigation} from '../navigation/DetailPageNavigation';
import {
    useConnectionIdFromCache,
    useZone,
} from '../hooks/useZone';
import config from '../config';

// FNP-1191 — READ HALF. No edit mode and no delete: the FNP-1184 schema has no
// zone mutation, and the port methods behind one do not exist yet.
//
// ─── RESERVED FOR THE CRUD FOLLOW-UP ───
// An Edit and a Delete tool belong in `tools` below, each gated on
// useZoneWriteCapabilities(connectionId) — canWriteRecords and canDeleteZones
// respectively. The capability plumbing and the hiding decision are already
// built and tested; the follow-up adds the controls, not the wiring.
export const DetailPage = () => {

    const {zoneId} = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    if (typeof zoneId === 'undefined') {
        throw new Error('zoneId is required parameter');
    }

    // The id arrives percent-encoded from the table link. `DnsZone.id` is opaque
    // and the adapter is free to mint one containing characters that are unsafe
    // in a path segment, so decoding is what keeps the link and the fetch in
    // agreement rather than relying on today's shape.
    const id = decodeURIComponent(zoneId);

    // ⚠️ HOW connectionId IS RESOLVED, AND WHY IT HAS TO BE.
    //
    // The route is `/zones/:zoneId` (FNP-1186 registered it; this ticket does
    // not edit routes.tsx), but `getDnsZone(connectionId: ID!, id: ID!)` needs
    // both — a zone id is only meaningful against the connection that minted
    // it, and the same zone name under two Route 53 connections is two zones.
    //
    //  1. `?connectionId=` — every link this module renders carries one, so the
    //     normal in-app path always hits this. It also makes the URL shareable
    //     and reload-safe, which the cache alone cannot do.
    //  2. The list cache — covers a link that arrived without the parameter
    //     while the index pages are still cached (back-navigation, a
    //     same-session bookmark).
    //  3. Neither — the page says so instead of firing a query guaranteed to
    //     fail schema validation.
    //
    // A `/zones/:connectionId/:zoneId` route would be the cleaner answer and is
    // worth a follow-up; it needs an edit to the shared routes.tsx, which
    // FNP-1186 deliberately fenced off from this ticket.
    const connectionIdFromUrl = searchParams.get('connectionId') ?? undefined;
    const connectionIdFromCache = useConnectionIdFromCache(id);
    const connectionId = connectionIdFromUrl ?? connectionIdFromCache;

    const {
        record,
        loading,
        isError,
        notFound,
        unresolvedConnection,
    } = useZone(connectionId, id);

    // ⚠️ Checked BEFORE loading: with no connectionId the query never runs, so
    // a spinner here would spin forever rather than explain itself.
    if (unresolvedConnection) {
        return (
            <Alert color={'yellow'} title={I18n.get('Cannot open this zone directly')}>
                {I18n.get('A zone can only be read through the connection it belongs to, and this link does not name one. Open the zone from the Zones list.')}
            </Alert>
        );
    }

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading zone...Please wait')}/>;
    }

    // The two failures read differently to an operator: one is "the API broke",
    // the other is "that zone is not on that connection".
    if (isError) {
        return (
            <Alert color={'red'} title={I18n.get('Could not load zone')}>
                {I18n.get('The domains API did not return this zone.')}
            </Alert>
        );
    }

    if (notFound || !record) {
        return (
            <Alert color={'yellow'} title={I18n.get('Zone not found')}>
                {I18n.get('No zone with this id was found on that connection. It may have been removed at the provider.')}
            </Alert>
        );
    }

    return (
        <FormPageLayout
            title={record.name}
            pageNavigation={<DetailPageNavigation component={record}/>}
            tools={[
                <FlexiCloseButton
                    key={'close'}
                    onClick={() => navigate(config.basePath)}
                >
                    {I18n.get('Close')}
                </FlexiCloseButton>,
            ]}
        >
            <Grid>
                <Grid.Col span={{
                    base: 12,
                    lg: 4,
                }}>
                    <Profile record={record}/>
                </Grid.Col>
                <Grid.Col span={{
                    base: 12,
                    lg: 8,
                }}>
                    {/*
                      ─── RESERVED FOR FNP-1192: Records ───

                      The zone's DNS records nest here, in the wide column. The
                      route `/zones/:zoneId/records` is already registered
                      (FNP-1186) and `listDnsRecords(connectionId:, zoneId:)` is
                      on the schema, but the records module is FNP-1192's scope.

                      Left EMPTY rather than stubbed: a placeholder panel would
                      promise a view this ticket does not deliver.
                    */}
                </Grid.Col>
            </Grid>
        </FormPageLayout>
    );
};
